// ============================================================================
// Usage warning channel — ORG-USAGE-2026
//
// The pre-turn member limit gate computes a non-blocking heads-up when a
// member crosses 80% / 95% of a configured usage limit. The backend delivers
// it on the SSE `done` event (streaming lane) and on `task.result`
// (async/VM polling lane). This module is the single client-side fan-in:
// both lanes publish here, and the usage-limit banner subscribes so the
// warning appears IMMEDIATELY — mid-conversation — instead of waiting for
// the member's next navigation to refetch limit status.
// ============================================================================

export const USAGE_WARNING_EVENT = 'prest0n:usage-warning';

export interface TurnUsageWarning {
  level: 'approaching' | 'critical' | 'reached' | string;
  message: string;
  ratio: number;
  used: number;
  limit: number;
  resetMs: number;
  windowLabel: string;
}

function isTurnUsageWarning(value: unknown): value is TurnUsageWarning {
  if (!value || typeof value !== 'object') return false;
  const w = value as Record<string, unknown>;
  return typeof w.message === 'string' && typeof w.ratio === 'number';
}

/** Publish a warning received from any turn lane. Safe to call with junk. */
export function publishUsageWarning(warning: unknown): void {
  if (!isTurnUsageWarning(warning)) return;
  if (typeof window === 'undefined') return;
  try {
    window.dispatchEvent(new CustomEvent<TurnUsageWarning>(USAGE_WARNING_EVENT, { detail: warning }));
  } catch {
    /* a display heads-up must never break the turn pipeline */
  }
}

/** Subscribe to pushed warnings. Returns an unsubscribe function. */
export function subscribeUsageWarnings(
  listener: (warning: TurnUsageWarning) => void
): () => void {
  if (typeof window === 'undefined') return () => undefined;
  const handler = (event: Event) => {
    const detail = (event as CustomEvent<TurnUsageWarning>).detail;
    if (isTurnUsageWarning(detail)) listener(detail);
  };
  window.addEventListener(USAGE_WARNING_EVENT, handler);
  return () => window.removeEventListener(USAGE_WARNING_EVENT, handler);
}
